import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import Navbar from "../components/Navbar";
import { useAuth, fmtErr } from "../lib/auth";
import { toast } from "sonner";
import { Hospital, ShieldCheck, Clock, Users, ArrowRight } from "lucide-react";

const TRUST_POINTS = [
  { icon: ShieldCheck, label: "NIMHANS & AIIMS-trained specialists" },
  { icon: Clock,       label: "Reports in 48 hours (12 h urgent)" },
  { icon: Users,       label: "Bulk submissions & per-hospital case feed" },
];

export default function HospitalSignup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    organization_name: "", organization_city: "", organization_state: "Telangana",
    organization_address: "", organization_phone: "", contact_designation: "Coordinator",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    if (!form.organization_name.trim() || !form.organization_city.trim()) {
      setErr("Organization name and city are required"); return;
    }
    setBusy(true);
    try {
      await signup({
        ...form,
        role: "hospital",
        phone: form.organization_phone || null,
      });
      toast.success("Application submitted — admin will review within 24 hours");
      nav("/hospital/pending", { state: { name: form.organization_name, email: form.email } });
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message || "Could not create account");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-200">
              <Hospital className="w-3.5 h-3.5"/> Hospital partner program
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
              Add a NeuroOpinion second-opinion layer to your hospital.
            </h1>
            <p className="text-slate-600 mt-3">
              Refer complex brain & spine cases to our verified panel without your team needing to email
              files or chase doctors. Per-patient consent, MCI/NMC-compliant reports, and a hospital-only
              feed of every case you submit.
            </p>
            <div className="mt-6 space-y-3">
              {TRUST_POINTS.map((p) => (
                <div key={p.label} className="flex items-start gap-3 text-sm">
                  <span className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 grid place-items-center shrink-0">
                    <p.icon className="w-4.5 h-4.5"/>
                  </span>
                  <div className="text-slate-700 pt-1.5">{p.label}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-sm text-slate-500">
              Already a partner? <Link to="/login" className="text-blue-700 font-semibold hover:underline">Sign in</Link>
            </div>
          </div>

          <Card className="lg:col-span-3 p-6 border-slate-200">
            <h2 className="font-heading font-semibold text-slate-900 text-lg">Create your hospital account</h2>
            <p className="text-xs text-slate-500 mt-1">Admin review takes &lt; 24 hours. We may call you on the registered phone to confirm.</p>

            <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-4">
              <Field label="Hospital / clinic name *" v={form.organization_name} set={(v) => set("organization_name", v)} testId="h-org" />
              <Field label="City *" v={form.organization_city} set={(v) => set("organization_city", v)} testId="h-city" />
              <Field label="State" v={form.organization_state} set={(v) => set("organization_state", v)} testId="h-state" />
              <Field label="Hospital phone" v={form.organization_phone} set={(v) => set("organization_phone", v)} testId="h-phone" placeholder="+91 …" />
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Hospital address</Label>
                <Textarea value={form.organization_address} onChange={(e) => set("organization_address", e.target.value)}
                          rows={2} className="mt-1" data-testid="h-address"/>
              </div>

              <div className="sm:col-span-2 my-1 border-t border-slate-100 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Primary contact
              </div>

              <Field label="Your name *" v={form.name} set={(v) => set("name", v)} testId="h-name" />
              <Field label="Designation" v={form.contact_designation} set={(v) => set("contact_designation", v)} testId="h-designation" />
              <Field label="Work email *" v={form.email} set={(v) => set("email", v)} type="email" testId="h-email" />
              <Field label="Password *" v={form.password} set={(v) => set("password", v)} type="password" testId="h-password" placeholder="Min 8 characters"/>

              {err && (
                <div className="sm:col-span-2 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid="h-signup-error">
                  {err}
                </div>
              )}

              <div className="sm:col-span-2 mt-1 flex items-center justify-between gap-2">
                <p className="text-[11px] text-slate-500">
                  By continuing you agree to the <Link to="/terms" className="text-blue-700">Terms</Link>{" "}
                  and our <Link to="/privacy" className="text-blue-700">B2B data handling</Link> policy.
                </p>
                <Button type="submit" disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="h-signup-submit">
                  {busy ? "Submitting…" : "Apply"} <ArrowRight className="w-4 h-4 ml-2"/>
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, set, type = "text", placeholder, testId }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <Input type={type} value={v} onChange={(e) => set(e.target.value)} placeholder={placeholder} className="mt-1" data-testid={testId}/>
    </div>
  );
}

export function HospitalPending() {
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar/>
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-100 text-blue-700 grid place-items-center">
          <Hospital className="w-8 h-8"/>
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-4">Application received</h1>
        <p className="text-slate-600 mt-3 max-w-md mx-auto">
          Our admin team reviews hospital applications within 24 hours. We&apos;ll email you the moment your
          account is approved — then you can sign in and submit your first case.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Link to="/"><Button variant="outline">Back to home</Button></Link>
          <a href="https://wa.me/918317674160?text=Hi%2C%20I%27ve%20just%20applied%20for%20a%20hospital%20partner%20account.">
            <Button className="bg-green-600 hover:bg-green-700">Speak to us on WhatsApp</Button>
          </a>
        </div>
      </div>
    </div>
  );
}
